import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../../../services/notification.service';

import { AccountInfo } from '@azure/msal-browser';
import { AccountInfoService } from '../../../../services/account-info.service';
import { INotification, INotifications,
  IReply,
  IUpdateNotification, } from '../../../../models/template/notifications-model.interface';
import { GlobalTemplateService } from '../../../../services/global-template.service';
import { PinService } from '../../../../services/pin.service';
import { SignalRService } from '../../../../services/signalr.service';
import { ToastBarService } from '../../../../services/toast-bar.service';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { IUser } from '../../../../models/other/user.interface';

@Component({
    selector: 'app-notification',
    imports: [FormsModule, ReactiveFormsModule, CommonModule], // Import NgModel for template binding
    templateUrl: './notification.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './notification.component.scss'
})
export class NotificationComponent implements OnInit, OnDestroy {
  @Input() notifications?: INotifications = [];
  @Input() blockId: string = '';
  @Input() templateId: string = '';
  /** Emits after every local mutation (new/edited/deleted comment or reply,
   *  or a SignalR-triggered refetch) - this component only ever mutates its
   *  own local `notifications` field, so a parent that reuses this instance
   *  across different blocks (e.g. destroying/recreating it on selection
   *  change) needs this to write the result back onto its own block model;
   *  otherwise the parent's copy silently falls behind and a freshly
   *  (re)created instance shows stale/incomplete data again. */
  @Output() notificationsChange = new EventEmitter<INotifications>();

  private _destroy$ = new Subject<void>();

  user?: AccountInfo;

  newCommentText: string | null = null;
  onReplyNotificationId?: string | null = null;
  newReplyText: string | null = null;

  editingNotificationId: string | null = null;
  updatedNotificationText: string | null = null;
  isNew: boolean = false;
  private subscription: Subscription = new Subscription();

  constructor(
    private notificationService: NotificationService,
    private accountInfoService: AccountInfoService,
    private globalTemplateService: GlobalTemplateService,
    private signalRService: SignalRService,
    private pinService: PinService,
    private toastBarService: ToastBarService
  ) {}
  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  ngOnInit() {
    if (!this.notifications) {
      this.notifications = [];
    }

    this.accountInfoService.accountInfo$
      .pipe(takeUntil(this._destroy$))
      .subscribe((data) => {
        this.user = data;
      });
    this.templateId = this.globalTemplateService.getTemplateId();
    // console.log('is new ', this.isNew);
    // subscribe to new drop pins
    this.pinService.data$.pipe(takeUntil(this._destroy$)).subscribe((pin) => {
      // filters out pins only meant for this component to prevent duplication
      if (pin?.blockID == this.blockId) {
        this.sendComment(pin);
      }
    });

    // Listen for new notifications
    // tu dolazi error
    this.signalRService.addNotificationListener(
      (notificationId: string, blockId: string) => {
        // Add new notification to the list
        this.fetchNotifications(blockId); // Optionally, refresh notifications from server
        this.pinService.setBlockId(blockId);
      }
    );

    // Listen for new replies
    this.signalRService.addReplyListener(
      (notificationId, replyText, replyId, userInfo) => {
        // Update the relevant notification with new reply
        this.addReplyToNotification(
          notificationId,
          replyText,
          replyId,
          userInfo
        );
      }
    );

    this.subscription.add(
      this.globalTemplateService
        .getIsNew()
        .pipe(takeUntil(this._destroy$))
        .subscribe((isNew: boolean) => {
          this.isNew = isNew;
        })
    );
  }

  highlightPin(notificationId?: string): void {
    // get pin
    const pin = document.querySelector(
      `[data-notification-id="${notificationId}"]`
    );
    // scroll into view
    pin?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // add class that flickers the pin
    pin?.classList.add('flicker-effect');
    setTimeout(() => {
      pin?.classList.remove('flicker-effect');
    }, 3000);
  }

  fetchNotifications(blockId: string) {
    if (blockId === this.blockId) {
      this.notificationService
        .getNotification(blockId)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (response: INotification[]) => {
            if (blockId == this.blockId) {
              this.notifications = response.sort(
                (a, b) =>
                  (b.createdDate ? new Date(b.createdDate).getTime() : 0) -
                  (a.createdDate ? new Date(a.createdDate).getTime() : 0)
              );
              this.pinService.sendNewPin(this.notifications);
              this.notificationsChange.emit(this.notifications);
              // console.log("sending notifications: ", this.notifications)
            }
          },
          error(err) {
            console.error(err);
          },
        });
    }
  }

  addReplyToNotification(
    notificationId: string,
    replyText: string,
    replyId: string,
    userInfo: IUser
  ): void {
    const notification = this.notifications?.find(
      (n) => n.notificationId === notificationId
    );
    if (notification) {
      notification.replys?.push({
        replyText: replyText,
        replyId: replyId,
        userInfo: userInfo,
      });
      this.notificationsChange.emit(this.notifications);
    }
  }

  sendComment(pin?: any) {
    // remove focus on elements
    let doc = document.activeElement as HTMLElement;
    if (doc && doc.classList.contains('notification-text-input')) {
      doc?.blur();
    }

    // disable sending empty comments
    if (this.newCommentText == null) {
      return;
    }

    if (this.newCommentText.trim() == '') {
      this.toastBarService.showToast({
        severity: 'error',
        summary: 'Error',
        detail: 'Comment cannot be empty',
      });
      return;
    }

    const createNotificationData: INotification = {
      templateId: this.templateId,
      blockId: this.blockId,
      notificationText: this.newCommentText?.trim() ?? '-',
      userInfo: {
        name: this.user?.name,
        username: this.user?.username,
      },
      xCords: pin?.xCords || 0,
      yCords: pin?.yCords || 0,
      pinId: pin?.pinId || '',
      createdDate: '',
      notificationId: '',
      replys: [],
      modifiedDate: '',
    };

    this.notificationService
      .createNotification(createNotificationData)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response: INotification) => {
          const newNotofication: INotification = {
            notificationText: response.notificationText ?? '',
            modifiedDate: response.modifiedDate ?? '',
            createdDate: response.createdDate,
            userInfo: response.userInfo ?? '',
            notificationId: response.notificationId ?? '',
            blockId: response.blockId ?? '',
            templateId: response.templateId ?? '',
            replys: response.replys ?? [],
            xCords: response.xCords,
            yCords: response.yCords,
            pinId: response.pinId,
          };

          this.notifications = [...this.notifications!, newNotofication].sort(
            (a, b) =>
              (b.createdDate ? new Date(b.createdDate).getTime() : 0) -
              (a.createdDate ? new Date(a.createdDate).getTime() : 0)
          );
          this.newCommentText = null;
          this.pinService.sendNewPin(this.notifications);
          this.notificationsChange.emit(this.notifications);
        },
        error(err) {
          console.error(err);
        },
      });
  }

  updateNotification(event: Event, notificationId: string) {
    event.preventDefault();

    const notification: INotification | undefined = this.notifications?.find(
      (notification) => notification.notificationId === notificationId
    );
    const updateNotificationData: IUpdateNotification = {
      notificationText:
        this.updatedNotificationText?.trim() ?? notification?.notificationText,
      userInfo: {
        name: this.user?.name ?? '-',
        username: this.user?.username ?? '-',
      },
    };

    this.notificationService
      .updateNotification(notificationId, updateNotificationData)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response: INotification) => {
          console.info('Notification updated successfully -> ', response);

          const index = this.notifications!.findIndex(
            (notification) => notification.notificationId === notificationId
          );
          if (index !== -1) {
            this.notifications![index] = {
              ...this.notifications![index],
              notificationText: this.updatedNotificationText ?? '',
            };

            this.notifications = [...this.notifications!].sort(
              (a, b) =>
                (b.createdDate ? new Date(b.createdDate).getTime() : 0) -
                (a.createdDate ? new Date(a.createdDate).getTime() : 0)
            );
          }

          this.updatedNotificationText = null;
          this.editingNotificationId = null;

          this.pinService.sendNewPin(this.notifications);
          this.notificationsChange.emit(this.notifications);
        },
        error(err) {
          console.error('Error updating notification', err);
        },
      });
  }

  addReply(event: Event, notificationId: string) {
    event.preventDefault();

    if (!notificationId || !this.newReplyText?.trim()) {
      return;
    }

    const replyData: IReply = {
      replyText: this.newReplyText.trim(),
      userInfo: {
        name: this.user?.name,
        username: this.user?.username,
      },
    };

    this.notificationService
      .createReply(notificationId, replyData)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response: INotification) => {
          const index = this.notifications?.findIndex(
            (notification) => notification.notificationId === notificationId
          );

          if (index !== -1 && this.notifications) {
            this.notifications![index!] = {
              ...this.notifications![index!],
              replys: response.replys,
            };
          }

          this.newReplyText = null;
          this.onReplyNotificationId = null;
          this.notificationsChange.emit(this.notifications);
        },
        error(err) {
          console.error('Error updating reply', err);
        },
      });
  }

  toggleReplyInput(event: Event, notificationId?: string): void {
    event.preventDefault();
    if (this.onReplyNotificationId == notificationId) {
      this.onReplyNotificationId = null;
    } else {
      this.onReplyNotificationId = notificationId;
    }
  }

  deleteReply(notificationId: string, replyId: string) {
    this.notificationService
      .deleteReply(notificationId, replyId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response: INotification) => {
          const index = this.notifications?.findIndex(
            (notification) => notification.notificationId == notificationId
          );

          if (index !== -1 && this.notifications) {
            this.notifications![index!] = {
              ...this.notifications![index!],
              replys: response.replys,
            };
          }

          this.newReplyText = null;
          this.onReplyNotificationId = null;
          this.notificationsChange.emit(this.notifications);
        },
        error(err) {
          console.error('Error updating reply', err);
        },
      });
  }

  deleteNotification(notificationId: string, blockId: string) {
    this.notificationService
      .deleteNotification(notificationId, blockId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response: any) => {
          const index = this.notifications?.findIndex(
            (notification) => notification.notificationId === notificationId
          );

          if (index !== -1 && this.notifications) {
            this.notifications.splice(index!, 1);
          }
          this.newReplyText = null;
          this.onReplyNotificationId = null;

          this.pinService.sendNewPin(this.notifications);
          this.notificationsChange.emit(this.notifications);
        },
        error(err) {
          console.error('Error deleting notification', err);
        },
      });
  }

  editNotification(notificationId: string): void {
    if (notificationId === this.editingNotificationId) {
      this.editingNotificationId = null;
    } else {
      this.editingNotificationId = notificationId;
    }
    const notification = this.notifications?.find(
      (n) => n.notificationId === notificationId
    );
    if (notification) {
      this.updatedNotificationText = notification.notificationText;
    }
  }
}
